<p-toast></p-toast>

<app-page-shell>
    @if (isLoading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-hero
        eyebrow="Customs Clearance"
        [title]="isEditMode ? 'Edit Clearance Job' : 'New Clearance Job'"
        subtitle="Capture the job shell, parties, route, documents, and container references before opening the declaration workspace.">
        <div actions>
            <p-button type="button" label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true"
                (click)="cancel()"></p-button>
            <p-button type="button" [label]="isEditMode ? 'Update Clearance' : 'Create Clearance'" icon="pi pi-save"
                [loading]="isLoading" (click)="saveClearanceHeader()"></p-button>
        </div>
    </app-page-hero>

    <form [formGroup]="clearanceForm" class="clearance-form p-fluid" novalidate autocomplete="off" autocorrect="off"
        autocapitalize="off" spellcheck="off">
        <app-page-form-section icon="pi pi-briefcase" title="Job Parties"
            subtitle="Broker, shipper, consignee, ownership, and current clearance status.">
            <div class="form-grid">
                <div class="field col-span-full" style="grid-column: 1 / -1">
                    <app-commercial-party-picker
                        [owningCompanyId]="owningCompanyId"
                        [value]="commercialPartyReference"
                        [required]="true"
                        label="Bill-to Commercial Party"
                        (valueChange)="onCommercialPartyChange($event)">
                    </app-commercial-party-picker>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-datePicker inputId="clearanceDate" formControlName="clearanceDate" styleClass="w-full"
                                [showTime]="true"></p-datePicker>
                            <label for="clearanceDate">Clearance Date *</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select inputId="broker" formControlName="broker" [options]="customsBrokers"
                                optionLabel="name" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                            <label for="broker">Customs Broker *</label>
                        </p-iftalabel>
                        <p-inputGroupAddon>
                            <p-button type="button" icon="pi pi-plus" [text]="true" severity="secondary"
                                (click)="openQuickCreate('broker')"></p-button>
                        </p-inputGroupAddon>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <app-logistics-project-lookup inputId="logisticsProjectId" formControlName="logisticsProjectId"></app-logistics-project-lookup>
                            <label for="logisticsProjectId">Logistics Project</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-send"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select inputId="shipper" formControlName="shipper" [options]="shippers"
                                optionLabel="shipperName" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                            <label for="shipper">Shipper *</label>
                        </p-iftalabel>
                        <p-inputGroupAddon>
                            <p-button type="button" icon="pi pi-plus" [text]="true" severity="secondary"
                                (click)="openQuickCreate('shipper')"></p-button>
                        </p-inputGroupAddon>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select inputId="shipperContact" formControlName="shipperContact"
                                [options]="filteredShipperContacts.length ? filteredShipperContacts : shipperContacts"
                                optionLabel="name" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                            <label for="shipperContact">Shipper Contact</label>
                        </p-iftalabel>
                        <p-inputGroupAddon>
                            <p-button type="button" icon="pi pi-plus" [text]="true" severity="secondary"
                                (click)="openQuickCreate('shipperContact')"></p-button>
                        </p-inputGroupAddon>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select inputId="consignee" formControlName="consignee" [options]="consignees"
                                optionLabel="name" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                            <label for="consignee">Consignee *</label>
                        </p-iftalabel>
                        <p-inputGroupAddon>
                            <p-button type="button" icon="pi pi-plus" [text]="true" severity="secondary"
                                (click)="openQuickCreate('consignee')"></p-button>
                        </p-inputGroupAddon>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-id-card"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select inputId="consigneeContact" formControlName="consigneeContact"
                                [options]="consigneeContacts" optionLabel="name" [filter]="true" [showClear]="true"
                                styleClass="w-full"></p-select>
                            <label for="consigneeContact">Consignee Contact *</label>
                        </p-iftalabel>
                        <p-inputGroupAddon>
                            <p-button type="button" icon="pi pi-plus" [text]="true" severity="secondary"
                                (click)="openQuickCreate('consigneeContact')"></p-button>
                        </p-inputGroupAddon>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select inputId="status" formControlName="status" [options]="clearanceStatuses"
                                optionLabel="label" optionValue="label" [showClear]="true" styleClass="w-full"></p-select>
                            <label for="status">Status</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-file-edit"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="primaryContractNumber" formControlName="primaryContractNumber" type="text" pInputText>
                            <label for="primaryContractNumber">Primary Contract Number</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-file-edit"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="subContractNumber" formControlName="subContractNumber" type="text" pInputText>
                            <label for="subContractNumber">Sub-Contract Number</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
            </div>
        </app-page-form-section>

        <app-page-form-section icon="pi pi-file" title="References"
            subtitle="Carrier, Bayan, delivery order, and Incoterm references used to connect the job to filing documents.">
            <div class="form-grid">
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon><p-iftalabel><input id="bl_awb_number" formControlName="bl_awb_number" type="text" pInputText><label for="bl_awb_number">BL/AWB Number</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon><p-iftalabel><input id="hbl_number" formControlName="hbl_number" type="text" pInputText><label for="hbl_number">HBL Number</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon><p-iftalabel><input id="bayanNumber" formControlName="bayanNumber" type="text" pInputText><label for="bayanNumber">Bayan Number</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-receipt"></i></p-inputGroupAddon><p-iftalabel><input id="deliveryOrderNumber" formControlName="deliveryOrderNumber" type="text" pInputText><label for="deliveryOrderNumber">Delivery Order Number</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon><p-iftalabel><input id="jobNumber" formControlName="jobNumber" type="text" pInputText><label for="jobNumber">Job Number</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-globe"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="incoterm" formControlName="incoterm" [options]="incoterms" optionLabel="name" optionValue="name" [showClear]="true" styleClass="w-full"></p-select><label for="incoterm">Incoterm</label></p-iftalabel></p-inputGroup></div>
            </div>
        </app-page-form-section>

        <app-page-form-section icon="pi pi-map-marker" title="Route And Schedule"
            subtitle="Origin, destination, vessel, ETA, ETD, and final delivery location.">
            <div class="form-grid">
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-compass"></i></p-inputGroupAddon><p-iftalabel><input id="vesselName" formControlName="vesselName" type="text" pInputText><label for="vesselName">Vessel Name</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="loadingCountryId" formControlName="loadingCountryId" [options]="countries" optionLabel="name" optionValue="id" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="loadingCountryId">Loading Country *</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="loadingCityId" formControlName="loadingCityId" [options]="loadingCities" optionLabel="cityName" optionValue="id" [filter]="true" [showClear]="true" [disabled]="!clearanceForm.get('loadingCountryId')?.value" styleClass="w-full"></p-select><label for="loadingCityId">Point of Loading *</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="dischargeCountryId" formControlName="dischargeCountryId" [options]="countries" optionLabel="name" optionValue="id" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="dischargeCountryId">Discharge Country *</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="dischargeCityId" formControlName="dischargeCityId" [options]="dischargeCities" optionLabel="cityName" optionValue="id" [filter]="true" [showClear]="true" [disabled]="!clearanceForm.get('dischargeCountryId')?.value" styleClass="w-full"></p-select><label for="dischargeCityId">Point of Discharge *</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar-clock"></i></p-inputGroupAddon><p-iftalabel><p-datePicker inputId="eta" formControlName="eta" styleClass="w-full"></p-datePicker><label for="eta">ETA</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon><p-iftalabel><p-datePicker inputId="etd" formControlName="etd" styleClass="w-full"></p-datePicker><label for="etd">ETD</label></p-iftalabel></p-inputGroup></div>
                <div class="field col-span-2">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-home"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select inputId="deliveryLocation" formControlName="deliveryLocation" appLazyLocationPicker
                                optionLabel="name" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                            <label for="deliveryLocation">Delivery Location *</label>
                        </p-iftalabel>
                        <p-inputGroupAddon>
                            <p-button type="button" icon="pi pi-plus" [text]="true" severity="secondary"
                                (click)="openQuickCreate('location')"></p-button>
                        </p-inputGroupAddon>
                    </p-inputGroup>
                </div>
            </div>
        </app-page-form-section>

        <app-page-form-section icon="pi pi-box" title="Containers And Customs Location"
            subtitle="Container references create linked container tracking cases when the clearance is saved.">
            <div class="form-grid customs-location-grid">
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="customsCountryId" formControlName="customsCountryId" [options]="countries" optionLabel="name" optionValue="id" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="customsCountryId">Customs Country *</label></p-iftalabel></p-inputGroup></div>
                <div class="field"><p-inputGroup><p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon><p-iftalabel><p-select inputId="customsCityId" formControlName="customsCityId" [options]="customsCities" optionLabel="cityName" optionValue="id" [filter]="true" [showClear]="true" [disabled]="!clearanceForm.get('customsCountryId')?.value" styleClass="w-full"></p-select><label for="customsCityId">Customs City *</label></p-iftalabel></p-inputGroup></div>
            </div>

            <div formArrayName="customsContainers" class="container-editor">
                <div class="container-editor-header">
                    <strong>Containers</strong>
                    <p-button type="button" label="Add Container" icon="pi pi-plus" size="small" (click)="addContainerRow()"></p-button>
                </div>
                @for (row of containerRows.controls; track $index; let i = $index) {
                    <div class="container-row" [formGroupName]="i">
                        <p-inputGroup class="container-field container-number-field">
                            <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
                            <p-iftalabel><input pInputText [id]="'containerNo' + i" formControlName="containerNo"><label [for]="'containerNo' + i">Container No *</label></p-iftalabel>
                        </p-inputGroup>
                        <p-inputGroup class="container-field">
                            <p-inputGroupAddon><i class="pi pi-table"></i></p-inputGroupAddon>
                            <p-iftalabel><input pInputText [id]="'containerType' + i" formControlName="containerType"><label [for]="'containerType' + i">Type</label></p-iftalabel>
                        </p-inputGroup>
                        <p-inputGroup class="container-field">
                            <p-inputGroupAddon><i class="pi pi-lock"></i></p-inputGroupAddon>
                            <p-iftalabel><input pInputText [id]="'sealNo' + i" formControlName="sealNo"><label [for]="'sealNo' + i">Seal No</label></p-iftalabel>
                        </p-inputGroup>
                        <p-inputGroup class="container-field">
                            <p-inputGroupAddon><i class="pi pi-arrow-up-right"></i></p-inputGroupAddon>
                            <p-iftalabel><input pInputText type="number" [id]="'grossWeight' + i" formControlName="grossWeight"><label [for]="'grossWeight' + i">Gross Weight</label></p-iftalabel>
                        </p-inputGroup>
                        <p-inputGroup class="container-field">
                            <p-inputGroupAddon><i class="pi pi-arrow-down-left"></i></p-inputGroupAddon>
                            <p-iftalabel><input pInputText type="number" [id]="'netWeight' + i" formControlName="netWeight"><label [for]="'netWeight' + i">Net Weight</label></p-iftalabel>
                        </p-inputGroup>
                        <p-inputGroup class="container-field remarks-field">
                            <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                            <p-iftalabel><input pInputText [id]="'remarks' + i" formControlName="remarks"><label [for]="'remarks' + i">Remarks</label></p-iftalabel>
                        </p-inputGroup>
                        <p-button type="button" icon="pi pi-trash" severity="danger" [outlined]="true" (click)="removeContainerRow(i)"></p-button>
                    </div>
                }
            </div>
        </app-page-form-section>

        <app-page-form-section icon="pi pi-paperclip" title="Initial Documents"
            subtitle="Attach available job documents now; the workspace document checklist will track required, verified, and rejected compliance evidence.">
            <p-table [value]="customsClearanceFileTypes" styleClass="p-datatable-sm document-table">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Document Type</th>
                        <th>Current File</th>
                        <th>Upload</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-fileType>
                    <tr>
                        <td><span class="doc-type"><i [class]="fileType.icon || 'pi pi-file'"></i>{{ fileType.label }}</span></td>
                        <td>
                            @if (uploadedFileForType(fileType.value); as uploadedFile) {
                                <span class="uploaded-file">{{ uploadedFile.fileName }}</span>
                                <p-button type="button" icon="pi pi-times" [text]="true" severity="secondary"
                                    (click)="removeUploadedFile(fileType.value)"></p-button>
                            } @else {
                                <span class="empty-file">No file</span>
                            }
                            @if (uploadProgressByType[fileType.value] > 0) {
                                <p-progressBar [value]="uploadProgressByType[fileType.value]"></p-progressBar>
                            }
                        </td>
                        <td>
                            <p-fileUpload name="clearanceDocuments" mode="basic" [customUpload]="true"
                                [disabled]="uploadProgressByType[fileType.value] > 0"
                                (onSelect)="onFileUpload($event, fileType.value)"
                                [chooseLabel]="uploadedFileForType(fileType.value) ? 'Replace' : 'Upload'"
                                [chooseIcon]="uploadedFileForType(fileType.value) ? 'pi pi-refresh' : 'pi pi-cloud-upload'"></p-fileUpload>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-form-section>

        <div class="form-actions">
            <p-button type="button" label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true"
                (click)="cancel()"></p-button>
            <p-button type="button" [label]="isEditMode ? 'Update Clearance' : 'Create Clearance'" icon="pi pi-save"
                [loading]="isLoading" (click)="saveClearanceHeader()"></p-button>
        </div>
    </form>
</app-page-shell>

<app-party-quick-create-dialog
    [(visible)]="quickCreateVisible"
    [type]="quickCreateType"
    [countries]="countries"
    [owningCompany]="currentCompany()"
    [contactCompany]="quickCreateContactCompany()"
    [defaultCountryId]="defaultCountryId()"
    (created)="onQuickCreateCreated($event)"
    (failed)="quickCreateFailed($event)">
</app-party-quick-create-dialog>
